/**
 * Data layer for `kpi-visibility-per-report-per-role`. Thin typed fetch wrappers over /api/v1/kpi-visibility-per-report-per-role.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface KpiVisibilityPerReportPerRoleRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListKpiVisibilityPerReportPerRoleResponse {
	count: number;
	items: KpiVisibilityPerReportPerRoleRow[];
}

export interface CreateKpiVisibilityPerReportPerRoleBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/kpi-visibility-per-report-per-role";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListKpiVisibilityPerReportPerRoleResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListKpiVisibilityPerReportPerRoleResponse>(res);
}

export async function create(body: CreateKpiVisibilityPerReportPerRoleBody): Promise<KpiVisibilityPerReportPerRoleRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<KpiVisibilityPerReportPerRoleRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const kpiVisibilityPerReportPerRoleQueryKeys = {
	all: ["kpi-visibility-per-report-per-role"] as const,
	list: () => [...kpiVisibilityPerReportPerRoleQueryKeys.all] as const,
	detail: (id: string) => [...kpiVisibilityPerReportPerRoleQueryKeys.all, id] as const,
};
